/**
 * Entscheidungslogik für die Tastatur-Sprechtaste.
 *
 * Warum es sie gibt: Der Sprechknopf ist das Avatar-Video in der Seitenleiste.
 * In der Multi-Ansicht, im Dashboard, im Verlauf und in den Collab-Seiten wird
 * diese Leiste nicht gerendert — die Sitzung läuft dort seit #966 zwar weiter,
 * aber man kann nicht mehr sprechen. Ausgerechnet in der Übersichtsansicht, in
 * der Alex den meisten Überblick hat, war die Stimme damit stumm.
 *
 * Reine Funktionen, damit die Bedingungen prüfbar sind statt in einem
 * Event-Handler zu verschwinden: Genau hier entscheidet sich, ob ein Tastendruck
 * ein Mikrofon öffnet — das gehört getestet, nicht geglaubt.
 */

/**
 * Die Taste, physisch adressiert (`code`), nicht über das erzeugte Zeichen
 * (`key`). Auf einer deutschen, französischen oder Dvorak-Belegung liegt an
 * derselben Stelle dieselbe Taste; `key` wäre je nach Layout und Modifikator
 * etwas anderes. Vorschlag von CloudV2-Codex.
 */
export const PTT_CODE = 'Space';

/**
 * Nur die Eigenschaften des Ereignis-Ziels, auf die es hier ankommt.
 *
 * Bewusst strukturell statt `instanceof Element`: Das ist ohne Browser prüfbar
 * — die Testumgebung hier hat kein DOM, und eine Abhängigkeit dafür nachzurüsten
 * wäre ein hoher Preis für vier Zeilen Logik. Es ist zusätzlich robuster,
 * weil `instanceof` über Realm-Grenzen (iframe, eingebettete Ansicht) falsch
 * antwortet, während `tagName`/`closest` dort weiter stimmen.
 */
export interface PttTarget {
  tagName?: string;
  isContentEditable?: boolean;
  closest?: (selector: string) => unknown;
}

const asElementLike = (target: PttTarget | null | undefined): PttTarget | null =>
  target && (typeof target.tagName === 'string' || typeof target.closest === 'function') ? target : null;

/** Was ein Tastenereignis mitbringen muss, damit wir entscheiden können. */
export interface PttKeyEvent {
  code?: string;
  key?: string;
  repeat?: boolean;
  metaKey?: boolean;
  ctrlKey?: boolean;
  altKey?: boolean;
  target?: PttTarget | EventTarget | null;
}

const isSpace = (event: PttKeyEvent): boolean =>
  event.code === PTT_CODE || event.key === ' ' || event.key === 'Spacebar';

/**
 * Tippt der Bediener gerade irgendwo? Dann gehört die Leertaste dorthin und
 * niemals uns — ein Leerzeichen in einer Nachricht darf kein Mikrofon öffnen.
 *
 * `closest` deckt auch verschachtelte Knoten innerhalb eines editierbaren
 * Bereichs ab (Textknoten, <b>, <span>) — der CRDT-Editor rendert genau so.
 */
export function isTypingTarget(target: PttTarget | null | undefined): boolean {
  const el = asElementLike(target);
  if (!el) return false;
  const tag = (el.tagName || '').toUpperCase();
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (el.isContentEditable) return true;
  return Boolean(el.closest?.('input, textarea, select, [contenteditable="true"]'));
}

/**
 * Hat gerade ein Bedienelement den Fokus, das die Leertaste selbst benutzt?
 *
 * Ein fokussierter Knopf wird vom Browser mit der Leertaste ausgelöst. Ohne
 * diese Ausnahme würde ein Druck gleichzeitig das Mikrofon öffnen UND den Knopf
 * drücken, auf dem der Fokus zufällig noch liegt — etwa der Stopp-Knopf, den man
 * eben angeklickt hat. Hinweis von CloudV2-Codex.
 */
export function isActivatableTarget(target: PttTarget | null | undefined): boolean {
  const el = asElementLike(target);
  if (!el) return false;
  const tag = (el.tagName || '').toUpperCase();
  if (tag === 'BUTTON' || tag === 'SUMMARY') return true;
  return Boolean(el.closest?.('button, summary, a[href], [role="button"], [role="link"], [role="menuitem"]'));
}

/**
 * Darf dieser Tastendruck das Mikrofon öffnen?
 *
 * Fail-closed: Im Zweifel nein. Ein nicht erkannter Druck kostet einen zweiten
 * Versuch; ein fälschlich geöffnetes Mikrofon überträgt, was niemand senden
 * wollte — und im Gesprächsmodus geht daraus womöglich eine Nachricht an einen
 * Agenten hervor.
 */
export function shouldStartPtt(event: PttKeyEvent, sessionLive: boolean): boolean {
  if (!sessionLive) return false;
  if (!isSpace(event)) return false;
  // Auto-Repeat: Halten erzeugt eine Serie von keydown-Ereignissen. Nur das
  // erste zählt, sonst wird das Öffnen zigfach wiederholt.
  if (event.repeat) return false;
  // Mit Modifikator ist es ein anderes Kürzel (Cmd+Leertaste = Spotlight,
  // Ctrl+Leertaste = Eingabequelle). Das gehört dem Betriebssystem.
  if (event.metaKey || event.ctrlKey || event.altKey) return false;
  if (isTypingTarget(event.target as PttTarget)) return false;
  if (isActivatableTarget(event.target as PttTarget)) return false;
  return true;
}

/**
 * Muss dieser Tastendruck das Mikrofon schließen?
 *
 * Bewusst großzügiger als das Öffnen: Wir schließen auch dann, wenn wir nicht
 * sicher sind. Der teure Fehler ist das offene Mikrofon, nicht das geschlossene.
 * Deshalb prüft diese Seite weder Modifikator noch Fokus — lässt jemand die
 * Leertaste los, nachdem er zwischendurch in ein Feld geklickt hat, endet die
 * Aufnahme trotzdem.
 */
export function shouldStopPtt(event: PttKeyEvent, held: boolean): boolean {
  if (!held) return false;
  return isSpace(event);
}
